import { FC, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import ringsIcon from '../../assets/rings.svg';
import { FOCUS_PILL_CHROME } from './focusPillChrome';

interface AnimatedFocusPillProps {
  /** Accessible name and the label revealed on hover. */
  label?: string;
  /** Collapsed (icon-only) width in px. */
  collapsedWidth?: number;
  /** Expanded width in px — wide enough to fit the label. */
  expandedWidth?: number;
}

// Spring ported from the Figma Make prototype.
const SPRING = { type: 'spring' as const, stiffness: 320, damping: 28 };

/**
 * Persistent "focus" indicator that sits on the focused unit's header (US-8,
 * TEAMC-3148). Collapsed to a 28px bullseye badge; on hover it springs open to
 * reveal the label. The label is always the accessible name (role="img" +
 * aria-label) so keyboard / touch / screen-reader users get the meaning without
 * the hover, and prefers-reduced-motion users see the expanded pill with no motion.
 */
export const AnimatedFocusPill: FC<AnimatedFocusPillProps> = ({
  label = 'my focus',
  collapsedWidth = 28,
  expandedWidth = 98,
}) => {
  const [hovered, setHovered] = useState(false);
  const reduceMotion = useReducedMotion();
  const expanded = hovered || !!reduceMotion;

  return (
    <motion.div
      role="img"
      aria-label={label}
      data-testid="focus-indicator"
      className={`${FOCUS_PILL_CHROME} tw-relative tw-inline-flex tw-items-center tw-cursor-default tw-select-none`}
      style={{ height: 28 }}
      initial={false}
      animate={{ width: expanded ? expandedWidth : collapsedWidth }}
      transition={reduceMotion ? { duration: 0 } : SPRING}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
    >
      {/* Border ring as an overlay so the 2px/4px border stays crisp and outside the width spring. */}
      <span
        aria-hidden
        className="tw-absolute tw-pointer-events-none tw-border-solid tw-border-indigo-350"
        style={{ inset: '-2px -2px -4px -2px', borderWidth: '2px 2px 4px 2px', borderRadius: '18px 18px 18px 4px' }}
      />

      {/* Icon */}
      <span className="tw-shrink-0" style={{ paddingLeft: 4, paddingTop: 4, paddingBottom: 4 }}>
        <span className="tw-relative tw-block tw-overflow-hidden" style={{ width: 20, height: 20 }}>
          <img
            src={ringsIcon}
            alt=""
            width={16}
            height={16}
            className="tw-absolute tw-block"
            style={{ left: 2, top: 2 }}
          />
        </span>
      </span>

      {/* Revealed label — decorative (aria-label above carries the accessible name). */}
      <motion.span
        aria-hidden
        className="tw-pointer-events-none tw-whitespace-nowrap tw-shrink-0 tw-text-sm tw-font-bold tw-leading-4 tw-text-indigo-900"
        style={{ marginLeft: 4, paddingRight: 8 }}
        initial={false}
        animate={{ opacity: expanded ? 1 : 0, x: expanded ? 0 : -6 }}
        transition={
          reduceMotion ? { duration: 0 } : { opacity: { duration: 0.16, delay: hovered ? 0.07 : 0 }, x: SPRING }
        }
      >
        {label}
      </motion.span>
    </motion.div>
  );
};

export default AnimatedFocusPill;
